import React, { useState, useEffect } from 'react';
import N8nModal from './Modal';
import { getAiConnectors } from '../../services/api';

const formStyles = {
  form: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  label: { display: 'flex', flexDirection: 'column', gap: 4, color: 'var(--text-secondary)', fontSize: '0.9rem' },
  section: {
    padding: '1rem',
    background: 'var(--bg-primary)',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
  },
  sectionTitle: {
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-tertiary)',
    marginBottom: '0.75rem',
    paddingBottom: '0.35rem',
    borderBottom: '1px solid var(--border-color)',
  },
  sectionFields: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  input: {
    padding: '0.5rem 0.75rem',
    background: 'var(--card-bg)',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
  },
  textarea: {
    padding: '0.5rem 0.75rem',
    background: 'var(--card-bg)',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    minHeight: 80,
    resize: 'vertical',
  },
  select: {
    padding: '0.5rem 0.75rem',
    background: 'var(--card-bg)',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
  },
  actions: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' },
  btnSecondary: {
    padding: '0.5rem 1rem',
    background: 'transparent',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    cursor: 'pointer',
  },
  btnPrimary: {
    padding: '0.5rem 1rem',
    background: '#3498db',
    border: 'none',
    borderRadius: '6px',
    color: 'white',
    cursor: 'pointer',
  },
};

const WEBHOOK_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const PARAM_VALUE_TYPES = [
  { value: 'fixed', title: 'Fixed' },
  { value: 'variable', title: 'Variable (query.x, body.y)' },
  { value: 'setbymodel', title: 'Set by model' },
];

const AUTH_TYPES = [
  { value: 'none', label: 'No auth' },
  { value: 'api_key', label: 'API key' },
  { value: 'bearer', label: 'Bearer token' },
  { value: 'jwt_bearer', label: 'JWT bearer' },
  { value: 'basic', label: 'Basic auth' },
];

function ParamValueTypeIcons({ valueType, onChange }) {
  return (
    <span style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
      {PARAM_VALUE_TYPES.map((opt) => (
        <button
          key={opt.value}
          type="button"
          title={opt.title}
          onClick={() => onChange(opt.value)}
          style={{
            width: 28,
            height: 28,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            background: valueType === opt.value ? '#3498db' : 'var(--card-bg)',
            color: valueType === opt.value ? 'white' : 'var(--text-tertiary)',
            cursor: 'pointer',
            fontSize: '0.85rem',
          }}
        >
          {opt.value === 'fixed' && 'A'}
          {opt.value === 'variable' && '{}'}
          {opt.value === 'setbymodel' && '◇'}
        </button>
      ))}
    </span>
  );
}

function TriggerSettingsForm({ data, config, onSave, onClose }) {
  const [label, setLabel] = useState(data?.label ?? 'Trigger');
  const [triggerType, setTriggerType] = useState(config?.triggerType ?? 'manual');
  const [schedule, setSchedule] = useState(config?.schedule ?? '');
  const [webhookMethod, setWebhookMethod] = useState(config?.webhookMethod ?? 'POST');
  const [description, setDescription] = useState(config?.description ?? '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      label,
      config: {
        triggerType,
        schedule: triggerType === 'schedule' ? schedule : undefined,
        webhookMethod: triggerType === 'webhook' ? webhookMethod : undefined,
        description: description || undefined,
      },
    });
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} style={formStyles.form}>
      <label style={formStyles.label}>
        Názov uzla
        <input value={label} onChange={(e) => setLabel(e.target.value)} style={formStyles.input} />
      </label>
      <label style={formStyles.label}>
        Typ triggera
        <select value={triggerType} onChange={(e) => setTriggerType(e.target.value)} style={formStyles.select}>
          <option value="manual">Manual (tlačidlo Run)</option>
          <option value="schedule">Schedule (cron)</option>
          <option value="webhook">Webhook (HTTP)</option>
        </select>
      </label>
      {triggerType === 'schedule' && (
        <label style={formStyles.label}>
          Cron výraz
          <input value={schedule} onChange={(e) => setSchedule(e.target.value)} style={formStyles.input} placeholder="0 * * * *" />
        </label>
      )}
      {triggerType === 'webhook' && (
        <label style={formStyles.label}>
          HTTP metóda
          <select value={webhookMethod} onChange={(e) => setWebhookMethod(e.target.value)} style={formStyles.select}>
            {WEBHOOK_METHODS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>
      )}
      <label style={formStyles.label}>
        Popis (voliteľné)
        <input value={description} onChange={(e) => setDescription(e.target.value)} style={formStyles.input} />
      </label>
      <div style={formStyles.actions}>
        <button type="button" onClick={onClose} style={formStyles.btnSecondary}>Zrušiť</button>
        <button type="submit" style={formStyles.btnPrimary}>Uložiť</button>
      </div>
    </form>
  );
}

const MEMORY_OPTIONS = [
  { value: '', label: 'Bez pamäte' },
  { value: 'simple', label: 'Simple memory' },
];

function AgentSettingsForm({ data, config, connectors, onSave, onClose }) {
  const [label, setLabel] = useState(data?.label ?? 'Agent');
  const [systemPrompt, setSystemPrompt] = useState(config?.systemPrompt ?? '');
  const [connectorId, setConnectorId] = useState(config?.connectorId ?? '');
  const [model, setModel] = useState(config?.model ?? '');
  const [memory, setMemory] = useState(config?.memory ?? '');
  const [temperature, setTemperature] = useState(config?.temperature != null ? String(config.temperature) : '0.7');
  const [maxTokens, setMaxTokens] = useState(config?.maxTokens != null ? String(config.maxTokens) : '2048');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      label,
      config: {
        systemPrompt: systemPrompt || undefined,
        connectorId: connectorId || undefined,
        model: model || undefined,
        memory: memory || undefined,
        temperature: temperature ? parseFloat(temperature) : undefined,
        maxTokens: maxTokens ? parseInt(maxTokens, 10) : undefined,
      },
    });
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} style={formStyles.form}>
      <label style={formStyles.label}>Názov uzla
        <input value={label} onChange={(e) => setLabel(e.target.value)} style={formStyles.input} />
      </label>
      <label style={formStyles.label}>System prompt
        <textarea value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} style={formStyles.textarea} placeholder="You are an assistant..." />
      </label>
      <div style={formStyles.section}>
        <div style={formStyles.sectionTitle}>AI</div>
        <div style={formStyles.sectionFields}>
          <label style={formStyles.label}>
            AI konektor
            <select value={connectorId} onChange={(e) => setConnectorId(e.target.value)} style={formStyles.select}>
              <option value="">— žiadny —</option>
              {(connectors || []).map((c) => (
                <option key={c._id} value={c._id}>{c.name || c.provider} ({c.provider})</option>
              ))}
            </select>
          </label>
          <label style={formStyles.label}>
            Model
            <input value={model} onChange={(e) => setModel(e.target.value)} style={formStyles.input} placeholder="gpt-4o" />
          </label>
          <label style={formStyles.label}>
            Temperature
            <input type="number" min="0" max="2" step="0.1" value={temperature} onChange={(e) => setTemperature(e.target.value)} style={formStyles.input} />
          </label>
          <label style={formStyles.label}>
            Max tokens
            <input type="number" min="1" value={maxTokens} onChange={(e) => setMaxTokens(e.target.value)} style={formStyles.input} />
          </label>
        </div>
      </div>
      <div style={formStyles.section}>
        <div style={formStyles.sectionTitle}>Pamäť</div>
        <div style={formStyles.sectionFields}>
          <label style={formStyles.label}>
            <select value={memory} onChange={(e) => setMemory(e.target.value)} style={formStyles.select}>
              {MEMORY_OPTIONS.map((opt) => (
                <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>
      <div style={formStyles.actions}>
        <button type="button" onClick={onClose} style={formStyles.btnSecondary}>Zrušiť</button>
        <button type="submit" style={formStyles.btnPrimary}>Uložiť</button>
      </div>
    </form>
  );
}

function ParamRow({ param, onChange, onRemove }) {
  const { name = '', valueType = 'fixed', value = '' } = param;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: '0.5rem', alignItems: 'center' }}>
      <input value={name} onChange={(e) => onChange({ ...param, name: e.target.value })} style={formStyles.input} placeholder="názov" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          value={value}
          onChange={(e) => onChange({ ...param, value: e.target.value })}
          style={{ ...formStyles.input, flex: 1, minWidth: 0 }}
          placeholder={valueType === 'variable' ? 'query.x alebo body.y' : 'hodnota'}
        />
        <ParamValueTypeIcons valueType={valueType} onChange={(v) => onChange({ ...param, valueType: v })} />
      </div>
      <button type="button" onClick={onRemove} style={formStyles.btnSecondary}>Odstrániť</button>
    </div>
  );
}

function ApiSettingsForm({ data, config, onSave, onClose }) {
  const [label, setLabel] = useState(data?.label ?? 'API');
  const [description, setDescription] = useState(config?.description ?? '');
  const [method, setMethod] = useState(config?.method ?? 'GET');
  const [url, setUrl] = useState(config?.url ?? '');
  const [headersJson, setHeadersJson] = useState(config?.headers ? JSON.stringify(config.headers, null, 2) : '{}');
  const [headerParams, setHeaderParams] = useState(
    Array.isArray(config?.headerParams) && config.headerParams.length > 0
      ? config.headerParams.map((p) => ({ name: p.name ?? '', valueType: p.valueType ?? 'fixed', value: p.value ?? '' }))
      : []
  );
  const [body, setBody] = useState(config?.body ?? '');
  const [getParams, setGetParams] = useState(
    Array.isArray(config?.getParams) && config.getParams.length > 0
      ? config.getParams.map((p) => ({ name: p.name ?? '', valueType: p.valueType ?? 'fixed', value: p.value ?? '' }))
      : []
  );
  const [bodyParams, setBodyParams] = useState(
    Array.isArray(config?.bodyParams) && config.bodyParams.length > 0
      ? config.bodyParams.map((p) => ({ name: p.name ?? '', valueType: p.valueType ?? 'fixed', value: p.value ?? '' }))
      : []
  );

  const addGetParam = () => setGetParams((prev) => [...prev, { name: '', valueType: 'fixed', value: '' }]);
  const updateGetParam = (index, param) => setGetParams((prev) => prev.map((p, i) => (i === index ? param : p)));
  const removeGetParam = (index) => setGetParams((prev) => prev.filter((_, i) => i !== index));
  const addBodyParam = () => setBodyParams((prev) => [...prev, { name: '', valueType: 'fixed', value: '' }]);
  const updateBodyParam = (index, param) => setBodyParams((prev) => prev.map((p, i) => (i === index ? param : p)));
  const removeBodyParam = (index) => setBodyParams((prev) => prev.filter((_, i) => i !== index));

  const addHeaderParam = () => setHeaderParams((prev) => [...prev, { name: '', valueType: 'fixed', value: '' }]);
  const updateHeaderParam = (index, param) => setHeaderParams((prev) => prev.map((p, i) => (i === index ? param : p)));
  const removeHeaderParam = (index) => setHeaderParams((prev) => prev.filter((_, i) => i !== index));

  const [authType, setAuthType] = useState(config?.auth?.type ?? 'none');
  const [apiKeyHeaderName, setApiKeyHeaderName] = useState(config?.auth?.apiKeyHeaderName ?? 'X-API-Key');
  const [apiKeyValueType, setApiKeyValueType] = useState(config?.auth?.apiKey?.valueType ?? 'fixed');
  const [apiKeyValue, setApiKeyValue] = useState(config?.auth?.apiKey?.value ?? '');
  const [bearerValueType, setBearerValueType] = useState(config?.auth?.token?.valueType ?? 'fixed');
  const [bearerValue, setBearerValue] = useState(config?.auth?.token?.value ?? '');
  const [basicUserValueType, setBasicUserValueType] = useState(config?.auth?.username?.valueType ?? 'fixed');
  const [basicUserValue, setBasicUserValue] = useState(config?.auth?.username?.value ?? '');
  const [basicPassValueType, setBasicPassValueType] = useState(config?.auth?.password?.valueType ?? 'fixed');
  const [basicPassValue, setBasicPassValue] = useState(config?.auth?.password?.value ?? '');

  const handleSubmit = (e) => {
    e.preventDefault();
    let headers = {};
    try {
      headers = JSON.parse(headersJson || '{}');
    } catch {
      // keep empty
    }
    const cleanGet = getParams.filter((p) => (p.name ?? '').trim() !== '');
    const cleanBody = bodyParams.filter((p) => (p.name ?? '').trim() !== '');
    const cleanHeaderParams = headerParams.filter((p) => (p.name ?? '').trim() !== '');

    const auth =
      authType === 'none'
        ? undefined
        : authType === 'api_key'
          ? {
              type: 'api_key',
              apiKeyHeaderName: (apiKeyHeaderName || 'X-API-Key').trim() || 'X-API-Key',
              apiKey: { valueType: apiKeyValueType, value: apiKeyValue },
            }
          : authType === 'bearer'
            ? { type: 'bearer', token: { valueType: bearerValueType, value: bearerValue } }
            : authType === 'jwt_bearer'
              ? { type: 'jwt_bearer', token: { valueType: bearerValueType, value: bearerValue } }
              : {
                  type: 'basic',
                  username: { valueType: basicUserValueType, value: basicUserValue },
                  password: { valueType: basicPassValueType, value: basicPassValue },
                };

    onSave({
      label,
      config: {
        description: description || undefined,
        method,
        url: url || undefined,
        headers: Object.keys(headers).length ? headers : undefined,
        headerParams: cleanHeaderParams.length > 0 ? cleanHeaderParams : undefined,
        auth,
        body: cleanBody.length === 0 ? (body || undefined) : undefined,
        getParams: cleanGet.length > 0 ? cleanGet : undefined,
        bodyParams: cleanBody.length > 0 ? cleanBody : undefined,
      },
    });
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} style={formStyles.form}>
      <label style={formStyles.label}>Názov uzla
        <input value={label} onChange={(e) => setLabel(e.target.value)} style={formStyles.input} />
      </label>
      <label style={formStyles.label}>Popis
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} style={formStyles.textarea} rows={3} placeholder="Čo tento fragment robí" />
      </label>
      <label style={formStyles.label}>HTTP metóda
        <select value={method} onChange={(e) => setMethod(e.target.value)} style={formStyles.select}>
          {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </label>
      <label style={formStyles.label}>URL
        <input value={url} onChange={(e) => setUrl(e.target.value)} style={formStyles.input} placeholder="https://api.example.com/..." />
      </label>

      <div style={formStyles.section}>
        <div style={formStyles.sectionTitle}>Autorizácia</div>
        <div style={formStyles.sectionFields}>
          <label style={formStyles.label}>
            Typ
            <select value={authType} onChange={(e) => setAuthType(e.target.value)} style={formStyles.select}>
              {AUTH_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </label>

          {authType === 'api_key' && (
            <>
              <label style={formStyles.label}>
                Názov headeru
                <input value={apiKeyHeaderName} onChange={(e) => setApiKeyHeaderName(e.target.value)} style={formStyles.input} placeholder="X-API-Key" />
              </label>
              <div style={formStyles.label}>
                <span style={{ marginBottom: 4, display: 'block' }}>API key</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    value={apiKeyValue}
                    onChange={(e) => setApiKeyValue(e.target.value)}
                    style={{ ...formStyles.input, flex: 1, minWidth: 0 }}
                    placeholder={apiKeyValueType === 'variable' ? 'query.apiKey alebo body.apiKey' : 'hodnota'}
                  />
                  <ParamValueTypeIcons valueType={apiKeyValueType} onChange={(v) => setApiKeyValueType(v)} />
                </div>
              </div>
            </>
          )}

          {(authType === 'bearer' || authType === 'jwt_bearer') && (
            <div style={formStyles.label}>
              <span style={{ marginBottom: 4, display: 'block' }}>Token</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  value={bearerValue}
                  onChange={(e) => setBearerValue(e.target.value)}
                  style={{ ...formStyles.input, flex: 1, minWidth: 0 }}
                  placeholder={bearerValueType === 'variable' ? 'query.token alebo body.token' : 'hodnota'}
                />
                <ParamValueTypeIcons valueType={bearerValueType} onChange={(v) => setBearerValueType(v)} />
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>
                Posiela sa v headri <code>Authorization</code> ako <code>Bearer {'<token>'}</code>.
              </div>
            </div>
          )}

          {authType === 'basic' && (
            <>
              <div style={formStyles.label}>
                <span style={{ marginBottom: 4, display: 'block' }}>Username</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    value={basicUserValue}
                    onChange={(e) => setBasicUserValue(e.target.value)}
                    style={{ ...formStyles.input, flex: 1, minWidth: 0 }}
                    placeholder={basicUserValueType === 'variable' ? 'query.user alebo body.user' : 'hodnota'}
                  />
                  <ParamValueTypeIcons valueType={basicUserValueType} onChange={(v) => setBasicUserValueType(v)} />
                </div>
              </div>
              <div style={formStyles.label}>
                <span style={{ marginBottom: 4, display: 'block' }}>Password</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="password"
                    value={basicPassValue}
                    onChange={(e) => setBasicPassValue(e.target.value)}
                    style={{ ...formStyles.input, flex: 1, minWidth: 0 }}
                    placeholder={basicPassValueType === 'variable' ? 'query.pass alebo body.pass' : 'hodnota'}
                  />
                  <ParamValueTypeIcons valueType={basicPassValueType} onChange={(v) => setBasicPassValueType(v)} />
                </div>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>
                Posiela sa v headri <code>Authorization</code> ako <code>Basic base64(username:password)</code>.
              </div>
            </>
          )}
        </div>
      </div>

      <div style={formStyles.label}>
        <span style={{ marginBottom: 4, display: 'block' }}>GET parametre</span>
        {getParams.map((p, i) => (
          <ParamRow key={i} param={p} onChange={(param) => updateGetParam(i, param)} onRemove={() => removeGetParam(i)} />
        ))}
        <button type="button" onClick={addGetParam} style={{ ...formStyles.btnSecondary, marginTop: 6 }}>+ Pridať</button>
      </div>
      {(method === 'POST' || method === 'PUT' || method === 'PATCH') && (
        <div style={formStyles.label}>
          <span style={{ marginBottom: 4, display: 'block' }}>Body parametre</span>
          {bodyParams.map((p, i) => (
            <ParamRow key={i} param={p} onChange={(param) => updateBodyParam(i, param)} onRemove={() => removeBodyParam(i)} />
          ))}
          <button type="button" onClick={addBodyParam} style={{ ...formStyles.btnSecondary, marginTop: 6 }}>+ Pridať</button>
          {bodyParams.length === 0 && (
            <label style={{ ...formStyles.label, marginTop: 8 }}>
              Alebo raw body
              <textarea value={body} onChange={(e) => setBody(e.target.value)} style={formStyles.textarea} placeholder='{"key": "value"}' />
            </label>
          )}
        </div>
      )}
      <div style={formStyles.section}>
        <div style={formStyles.sectionTitle}>Headers</div>
        <div style={formStyles.sectionFields}>
          <div style={formStyles.label}>
            <span style={{ marginBottom: 4, display: 'block' }}>Header parametre</span>
            {headerParams.map((p, i) => (
              <ParamRow key={i} param={p} onChange={(param) => updateHeaderParam(i, param)} onRemove={() => removeHeaderParam(i)} />
            ))}
            <button type="button" onClick={addHeaderParam} style={{ ...formStyles.btnSecondary, marginTop: 6 }}>+ Pridať</button>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginTop: 6 }}>
              Ak zadáte aj autorizáciu, jej headre sa zlúčia s týmito (prípadné konflikty rieši posledný zápis).
            </div>
          </div>
          <label style={formStyles.label}>Advanced (JSON)
            <textarea value={headersJson} onChange={(e) => setHeadersJson(e.target.value)} style={formStyles.textarea} placeholder='{"X-Custom": "value"}' />
          </label>
        </div>
      </div>
      <div style={formStyles.actions}>
        <button type="button" onClick={onClose} style={formStyles.btnSecondary}>Zrušiť</button>
        <button type="submit" style={formStyles.btnPrimary}>Uložiť</button>
      </div>
    </form>
  );
}

function RunJsSettingsForm({ data, config, onSave, onClose }) {
  const [label, setLabel] = useState(data?.label ?? 'Run JS');
  const [description, setDescription] = useState(config?.description ?? '');
  const [code, setCode] = useState(config?.code ?? '// input = výstupy z predchádzajúcich uzlov\nreturn input;');
  const [timeoutSec, setTimeoutSec] = useState(config?.timeoutSec != null ? String(config.timeoutSec) : '5');

  const handleSubmit = (e) => {
    e.preventDefault();
    const sec = parseInt(timeoutSec, 10);
    onSave({
      label,
      config: {
        description: description || undefined,
        code: code || undefined,
        timeoutSec: Number.isFinite(sec) && sec > 0 ? sec : 5,
      },
    });
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} style={formStyles.form}>
      <label style={formStyles.label}>Názov uzla
        <input value={label} onChange={(e) => setLabel(e.target.value)} style={formStyles.input} />
      </label>
      <label style={formStyles.label}>Popis
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} style={formStyles.textarea} rows={3} placeholder="Čo kód robí" />
      </label>
      <label style={formStyles.label}>
        JavaScript kód
        <span style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginBottom: 4, display: 'block' }}>
          Premenná <code>input</code> = výstupy predchádzajúcich uzlov. Návratová hodnota = výstup tohto uzla.
        </span>
        <textarea
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={{ ...formStyles.textarea, fontFamily: 'monospace', minHeight: 160 }}
          spellCheck={false}
        />
      </label>
      <label style={formStyles.label}>Timeout (sekundy)
        <input type="number" min="1" max="30" value={timeoutSec} onChange={(e) => setTimeoutSec(e.target.value || '5')} style={formStyles.input} placeholder="5" />
      </label>
      <div style={formStyles.actions}>
        <button type="button" onClick={onClose} style={formStyles.btnSecondary}>Zrušiť</button>
        <button type="submit" style={formStyles.btnPrimary}>Uložiť</button>
      </div>
    </form>
  );
}

function AiModelSettingsForm({ data, config, connectors, onSave, onClose }) {
  const [label, setLabel] = useState(data?.label ?? 'AI model');
  const [connectorId, setConnectorId] = useState(config?.connectorId ?? '');
  const [model, setModel] = useState(config?.model ?? '');
  const [systemPrompt, setSystemPrompt] = useState(config?.systemPrompt ?? '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      label,
      config: {
        connectorId: connectorId || undefined,
        model: model || undefined,
        systemPrompt: systemPrompt || undefined,
      },
    });
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} style={formStyles.form}>
      <label style={formStyles.label}>Názov uzla
        <input value={label} onChange={(e) => setLabel(e.target.value)} style={formStyles.input} />
      </label>
      <label style={formStyles.label}>AI konektor
        <select value={connectorId} onChange={(e) => setConnectorId(e.target.value)} style={formStyles.select}>
          <option value="">— vyberte —</option>
          {(connectors || []).map((c) => (
            <option key={c._id} value={c._id}>{c.name || c.provider} ({c.provider})</option>
          ))}
        </select>
      </label>
      <label style={formStyles.label}>Model
        <input value={model} onChange={(e) => setModel(e.target.value)} style={formStyles.input} placeholder="gpt-4o" />
      </label>
      <label style={formStyles.label}>System prompt
        <textarea value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} style={formStyles.textarea} rows={5} placeholder="Inštrukcie pre model..." />
      </label>
      <div style={formStyles.actions}>
        <button type="button" onClick={onClose} style={formStyles.btnSecondary}>Zrušiť</button>
        <button type="submit" style={formStyles.btnPrimary}>Uložiť</button>
      </div>
    </form>
  );
}

export default function NodeSettingsModal({ node, onSave, onClose }) {
  const [connectors, setConnectors] = useState([]);

  useEffect(() => {
    getAiConnectors().then(setConnectors).catch(() => setConnectors([]));
  }, []);

  if (!node) return null;

  const data = node.data ?? {};
  const config = data.config ?? {};
  const typeLabels = {
    trigger: 'Nastavenia triggera',
    agent: 'Nastavenia agenta',
    api: 'Nastavenia API',
    ai_model: 'Nastavenia AI modelu',
    run_js: 'Nastavenia Run JS',
  };
  const title = typeLabels[node.type] ?? 'Nastavenia uzla';

  const handleSave = (updates) => {
    onSave({
      ...data,
      ...updates,
      config: { ...config, ...(updates.config || {}) },
    });
  };

  return (
    <N8nModal title={title} onClose={onClose} width={620}>
      {node.type === 'trigger' && <TriggerSettingsForm data={data} config={config} onSave={handleSave} onClose={onClose} />}
      {node.type === 'agent' && <AgentSettingsForm data={data} config={config} connectors={connectors} onSave={handleSave} onClose={onClose} />}
      {node.type === 'api' && <ApiSettingsForm data={data} config={config} onSave={handleSave} onClose={onClose} />}
      {node.type === 'ai_model' && <AiModelSettingsForm data={data} config={config} connectors={connectors} onSave={handleSave} onClose={onClose} />}
      {node.type === 'run_js' && <RunJsSettingsForm data={data} config={config} onSave={handleSave} onClose={onClose} />}
      {!['trigger', 'agent', 'api', 'ai_model', 'run_js'].includes(node.type) && (
        <p style={{ color: 'var(--text-tertiary)' }}>Pre tento typ uzla nie sú nastavenia.</p>
      )}
    </N8nModal>
  );
}
